import { useMemo } from 'react'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import cn from 'classnames'
import { PauseOutlined, PlusOutlined } from '@ant-design/icons'
import {
  getImagesFromVariantOrPlaceholder,
  IProductSearch,
} from '@core/api/Products/types'
import {
  LYSTypography,
  LYSCard,
  LYSCheckbox,
  LYSSpacer,
  LYSIcon,
} from '@core/components/Primitives'
import ProductDetailLink from '@core/components/ProductCollection/ProductDetailLink'
import ProductPrice from '@core/components/shared/ProductPrice'
import { useServices } from '@core/utils/ioc'
import { ProductContextProvider } from '@core/utils/models/product/ProductContext'
import style from './index.module.less'

interface Props {
  product: IProductSearch
  isSelected: boolean
  isLastItem: boolean
  onSelectionChange: (item: IProductSearch, isItemSelected: boolean) => void
}

const ProductBundleItem: React.FC<Props> = ({
  product,
  isSelected,
  isLastItem,
  onSelectionChange,
}) => {
  const { productContextService } = useServices()
  const productContext = useMemo(
    () =>
      productContextService.getProductSearchContext(
        product,
        product.mainVariant
      ),
    [productContextService, product]
  )

  const variantImages = getImagesFromVariantOrPlaceholder(product.mainVariant)

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    onSelectionChange(product, e.target.checked)
  }

  return (
    <ProductContextProvider productContext={productContext}>
      <LYSCard
        withBoxShadow={true}
        size={'small'}
        className={cn(style.bundleItemOuter, !isSelected && style.deselected)}
      >
        <LYSCheckbox
          checked={isSelected}
          className={style.checkbox}
          onChange={handleCheckboxChange}
          disabled={!productContext.rules.isPurchasable}
        />
        <div className={style.bundleItemInner}>
          <ProductDetailLink
            product={product}
            slug={product.mainVariant.slug}
            id={product.mainVariant.id}
          >
            <img
              className={style.image}
              src={variantImages[0].url}
              alt={product.name}
              title={product.name}
              loading="lazy"
              width={300}
              height={300}
            />
            <div className={style.productInfo}>
              <LYSTypography.Text visualAppearance={'h4'}>
                {product.name}
              </LYSTypography.Text>
              <LYSTypography.Text type={'secondary'}>
                {product.variationName}
              </LYSTypography.Text>
            </div>
          </ProductDetailLink>
          <LYSTypography.Text className={style.productDescription}>
            {product.shortDescription}
          </LYSTypography.Text>
          <LYSSpacer />
          <LYSTypography.Text className={style.price}>
            <ProductPrice
              price={product.mainVariant.packagingUnits[0].price}
              priceClass={style.priceValue}
              unitPrice={product.mainVariant.packagingUnits[0].unitPrice}
              strikePrice={
                product.mainVariant.packagingUnits[0].strikeThroughPrice?.price
              }
            />
          </LYSTypography.Text>
          {isLastItem ? (
            <LYSIcon component={PauseOutlined} className={style.iconEqual} />
          ) : (
            <LYSIcon component={PlusOutlined} className={style.iconPlus} />
          )}
        </div>
      </LYSCard>
    </ProductContextProvider>
  )
}

export default ProductBundleItem
